import { Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Tab, Tabs } from '@blueprintjs/core';
import { toast } from 'sonner';
import {
  cancelBatch, costOf, discardDraft, downloadCsv, fetchBatchSummary, fetchCreditExport, fetchDuplicateReport, fetchExcludedExport,
  fetchNeverFillFields, fetchSecondaryReport, fillBlanks, isVerifyKind, resumeBatch, retryFailedVerification,
  type BatchDetail, type ExportFilter,
} from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import AssignPanel from './AssignPanel';
import AssignmentsView from './AssignmentsView';
import QuickAssign from './QuickAssign';
import LeadsBrowser from './LeadsBrowser';
import HoldQueue from './HoldQueue';
import { FileChip, FilePreviewDialog } from './FileChip';
import SourceLocationNote from './SourceLocationNote';
import ImportCostFields from './ImportCost';
import VerificationRunDialog from './VerificationRunDialog';
import ExportDialog from './ExportDialog';
import { useVerifyLeads } from './VerifyLeads';
import { Card, Empty, JobBanner, RowLedger, Stat, StatusPill, fmtDay, fmtInt, fmtMoney, fmtWhen, useConfirm } from './ui';

const TABS = ['leads', 'assignments', 'holds', 'verification', 'details'] as const;
type TabId = typeof TABS[number];

/** Workspace of an import that was started: progress, leads, assignments,
 * review queue, verification and the delivery's details. */
export default function BatchWorkspace({ batch, onOpenBatch, onOpenLead }: { batch: BatchDetail; onOpenBatch: (id: number) => void; onOpenLead: (id: number) => void }) {
  const queryClient = useQueryClient();
  const committed = batch.status === 'Committed';
  // ?view= keeps the tab in links and history. Without it the leads open once
  // the import finishes, so a running import switches over by itself.
  const [params, setParams] = useSearchParams();
  const view = params.get('view');
  const tab: TabId = TABS.find((t) => t === view) ?? (committed ? 'leads' : 'details');
  const setTab = (t: TabId) => setParams((p) => { const n = new URLSearchParams(p); n.set('view', t); return n; }, { replace: true });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: leadImportKeys.all });

  const resume = useMutation({ mutationFn: () => resumeBatch(batch.id), onSuccess: invalidate, onError: (e) => toast.error((e as Error).message) });
  // A reconciliation failure means the staged rows no longer match the file, so there is nothing safe to resume.
  const resumable = batch.stage_error?.code !== 'reconciliation_failed';
  const cancel = useMutation({ mutationFn: () => cancelBatch(batch.id), onSuccess: invalidate, onError: (e) => toast.error((e as Error).message) });
  // A stopped import that was never accepted holds no leads; discarding it removes the batch and its staging.
  const discard = useMutation({
    mutationFn: () => discardDraft(batch.id),
    onSuccess: () => { toast.success('Import discarded. The uploaded file is kept.'); invalidate(); onOpenBatch(0); },
    onError: (e) => toast.error((e as Error).message),
  });
  const commitJob = batch.job?.kind === 'commit' ? batch.job : null;
  const [confirm, confirmDialog] = useConfirm();

  return (
    <div className="li-batch">
      <header className="li-batch__head">
        <div>
          <div className="li-row">
            <h2>{batch.source}{batch.sub_lead_source ? ` (${batch.sub_lead_source})` : ''}, {fmtDay(batch.batch_date)}</h2>
            <StatusPill status={batch.status} />
          </div>
          <p className="li-batch__meta li-filerow">
            <FileChip batchId={batch.id} name={batch.file?.original_filename ?? batch.file_name} bytes={batch.file?.byte_size} />
            <span>{committed ? `Imported ${fmtWhen(batch.committed_at)}` : `Started ${fmtWhen(batch.accepted_at)}`}</span>
          </p>
        </div>
        <div className="li-row">
          {batch.accepted_at && <ExportButton batch={batch} />}
          {batch.status === 'Committing' && (
            <button type="button" className="li-btn li-btn--quiet li-btn--danger" disabled={cancel.isPending || !!batch.cancel_requested_at}
              onClick={async () => {
                if (await confirm({ title: 'Stop this import?', body: 'Leads already imported stay. You can resume later.', action: 'Stop import', danger: true })) cancel.mutate();
              }}>
              {batch.cancel_requested_at ? 'Stopping…' : 'Stop import'}
            </button>
          )}
        </div>
      </header>
      {confirmDialog}

      {batch.status === 'Committing' && batch.cancel_requested_at && (
        <div className="li-banner li-banner--warn" role="status">
          <div className="li-banner__text"><b>Stopping.</b> The step in progress finishes first, then the import stops. Leads already imported stay.</div>
        </div>
      )}
      {batch.status === 'Committing' && (
        <JobBanner job={commitJob} stageError={batch.stage_error}
          idleText={`Importing. ${fmtInt(batch.commit_done)} of ${fmtInt(batch.commit_total)} phones done.`} />
      )}
      {batch.status === 'Failed' && (
        <div className="li-banner li-banner--danger" role="alert">
          <div className="li-banner__text">
            <b>The import stopped before finishing.</b> {batch.stage_error?.message}{' '}
            {fmtInt(batch.commit_done)} of {fmtInt(batch.commit_total)} phones were imported and stay.{resumable ? ' Resuming continues where it stopped.' : ''}
          </div>
          {resumable && <button type="button" className="li-btn li-btn--primary" disabled={resume.isPending} onClick={() => resume.mutate()}>Resume import</button>}
        </div>
      )}
      {batch.status === 'Cancelled' && (
        <div className="li-banner li-banner--muted">
          <div className="li-banner__text">
            <b>Stopped.</b> {batch.accepted_at ? `${fmtInt(batch.commit_done)} of ${fmtInt(batch.commit_total)} phones were imported before it stopped and stay.` : 'Nothing was imported.'}
          </div>
          {batch.accepted_at && <button type="button" className="li-btn li-btn--secondary" disabled={resume.isPending} onClick={() => resume.mutate()}>Resume import</button>}
          {!batch.accepted_at && (
            <button type="button" className="li-btn li-btn--quiet li-btn--danger" disabled={discard.isPending}
              onClick={async () => {
                if (await confirm({ title: 'Discard this import?', body: 'Nothing was imported. The uploaded file stays stored and can still be downloaded.', action: 'Discard import', danger: true })) discard.mutate();
              }}>
              Discard import
            </button>
          )}
        </div>
      )}

      <Tabs id="li-batch-tabs" selectedTabId={tab} onChange={(t) => setTab(t as TabId)} renderActiveTabPanelOnly className="li-tabs">
        <Tab id="leads" title={<span>Leads <span className="li-count">{fmtInt(batch.observation_count || batch.commit_done)}</span></span>}
          panel={<LeadsTab batch={batch} onOpenLead={onOpenLead} />} />
        <Tab id="assignments" title="Assignments" panel={<AssignmentsView batchId={batch.id} batchStatus={batch.status} batchJob={batch.job} canDistribute={batch.status === 'Committed'} />} />
        <Tab id="holds" title={<span>Needs review {batch.open_holds > 0 && <span className="li-count li-count--warn">{batch.open_holds}</span>}</span>}
          panel={<HoldQueue batchId={batch.id} onOpenLead={onOpenLead} />} />
        <Tab id="verification" title="Verification" panel={<VerificationTab batch={batch} onOpenLead={onOpenLead} />} />
        <Tab id="details" title="Details" panel={<DetailsTab batch={batch} onOpenLeads={() => setTab('leads')} />} />
      </Tabs>
    </div>
  );
}

/* ── Leads: spreadsheet-like view, assignment, verification ── */

function LeadsTab({ batch, onOpenLead }: { batch: BatchDetail; onOpenLead: (id: number) => void }) {
  const committed = batch.status === 'Committed';
  const summary = useQuery({ queryKey: leadImportKeys.summary(batch.id), queryFn: () => fetchBatchSummary(batch.id), ...queryPolicies.pagedData });
  const verify = useVerifyLeads();
  const [advanced, setAdvanced] = useState(false);

  if (!batch.accepted_at) return <Empty title="No leads yet">Leads appear here once the import starts.</Empty>;

  return (
    <>
      <LeadsBrowser
        batchId={batch.id}
        summary={summary.data}
        // Check columns stay hidden until this import has had a verification run.
        showChecks={batch.verification.runs.length > 0}
        onOpenLead={onOpenLead}
        renderActions={(v) => committed && v.selected.length > 0 && verify.buttons(v.selected, v.clearSelection)}
        // The plan for openers is the default; checked rows or Advanced open the full panel.
        renderAside={(v) => committed ? (!advanced && v.selectedIds.length === 0 && !v.allMatching ? (
          <QuickAssign batchId={batch.id} onAdvanced={() => setAdvanced(true)} onSaved={v.clearSelection} />
        ) : (
          <div className="li-stack">
            {advanced && <button type="button" className="li-btn li-btn--link li-assign__back" onClick={() => setAdvanced(false)}>Back to the openers plan</button>}
            <AssignPanel
            batchId={batch.id}
            sourceId={batch.lead_source}
            summary={summary.data}
            selectedIds={v.selectedIds}
            allMatching={v.allMatching}
            matching={v.total}
            viewFilter={v.filter}
            sort={v.sortKey}
            desc={v.desc}
            onSaved={v.clearSelection}
            />
          </div>
        )) : (
          <Card title="Give leads to a rep">
            <p className="li-muted">{batch.status === 'Committing' ? 'Available as soon as the import finishes.' : 'Available once the import has finished. Resume it first.'}</p>
          </Card>
        )}
      />
      {verify.dialog}
    </>
  );
}

/* ── Verification runs ────────────────────────────────── */

function runCounts(counts: Record<string, number> | null, reused: Record<string, number> | null, kind: string) {
  const get = (state: string) => Number(counts?.[`${kind}:${state}`] ?? 0);
  const c = { queued: get('queued'), sent: get('sent'), recorded: get('recorded'), failed: get('failed'), unknown: get('outcome_unknown') };
  const r = { reused: Number(reused?.[`${kind}:reused`] ?? 0), joined: Number(reused?.[`${kind}:joined`] ?? 0) };
  return { ...c, ...r, total: c.queued + c.sent + c.recorded + c.failed + c.unknown };
}

/** [spec B2] What the import's leads came back as, from the stored answers. */
function VerificationStats({ v }: { v: BatchDetail['verification'] }) {
  const n = (r: Record<string, number>, ...keys: string[]) => keys.reduce((s, k) => s + (r[k] ?? 0), 0);
  const phone = v.results.phone;
  const email = v.results.email;
  const phones = Object.values(phone).reduce((s, x) => s + x, 0);
  const emails = Object.values(email).reduce((s, x) => s + x, 0);
  const onImport = v.on_import ?? [];
  if (!onImport.length && phones + emails === 0) return null;
  return (
    <Card title="Results"
      subtitle={onImport.length
        ? `Chosen at import: ${onImport.map((k) => (k === 'phone' ? 'phones' : 'emails')).join(' and ')}.`
        : 'No checks were chosen at import.'}>
      <div className="li-stats li-stats--4" aria-label="Verification results">
        <Stat label="Phones checked" value={fmtInt(phones)} sub={`of ${fmtInt(v.phones)}`} />
        <Stat label="Mobile" value={fmtInt(n(phone, 'mobile'))} tone="good" />
        <Stat label="Landline" value={fmtInt(n(phone, 'fixed_line', 'fixed_line_or_mobile'))} />
        <Stat label="Bad phones" value={fmtInt(n(phone, 'invalid'))} tone="bad"
          sub={n(phone, 'voip', 'toll_free', 'unknown') ? `${fmtInt(n(phone, 'voip', 'toll_free', 'unknown'))} VoIP, toll free or unknown` : undefined} />
        <Stat label="Emails checked" value={fmtInt(emails)} sub={`of ${fmtInt(v.emails)}`} />
        <Stat label="Good emails" value={fmtInt(n(email, 'ok'))} tone="good"
          sub={n(email, 'catch_all') ? `${fmtInt(n(email, 'catch_all'))} more accept all mail` : undefined} />
        <Stat label="Bad emails" value={fmtInt(n(email, 'invalid', 'disposable'))} tone="bad"
          sub={n(email, 'unknown') ? `${fmtInt(n(email, 'unknown'))} unknown` : undefined} />
        <Stat label="Credits used" value={fmtInt(v.credits_used)} />
      </div>
    </Card>
  );
}

function VerificationTab({ batch, onOpenLead }: { batch: BatchDetail; onOpenLead: (id: number) => void }) {
  const queryClient = useQueryClient();
  const [openRun, setOpenRun] = useState<number | null>(null);
  const retry = useMutation({
    mutationFn: (run: number) => retryFailedVerification(run),
    onSuccess: (n) => { toast.success(`${fmtInt(n)} checks queued again.`); queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch.id) }); },
    onError: (e) => toast.error((e as Error).message),
  });
  const runs = batch.verification.runs;
  const verifyJob = isVerifyKind(batch.job?.kind) ? batch.job : null;
  return (
    <div className="li-stack">
      <JobBanner job={verifyJob} stageError={isVerifyKind(batch.stage_error?.stage) ? batch.stage_error : null} />
      <Card title="Verification" subtitle="Checks whether phones and emails are real, through the configured providers. Nobody is contacted.">
        <p>
          Select leads on the Leads tab and choose Verify. Each phone or email is checked once and the result is stored, so later runs and
          later imports reuse it at no cost. You can choose to check again when you start a run.
        </p>
        <p className="li-muted">
          Provider outages and network errors are retried automatically. Retry failed is for checks the provider refused outright.
          A failed check never removes a lead.
        </p>
      </Card>
      <VerificationStats v={batch.verification} />
      {runs.length === 0 ? (
        <Empty title="No verification runs yet">{fmtInt(batch.verification.phones)} phones and {fmtInt(batch.verification.emails)} well-formed emails can be checked.</Empty>
      ) : (
        <Card title="Runs" flush>
          <div className="li-tablewrap">
            <table className="li-table">
              <thead><tr><th>Run</th><th>Started</th><th className="r">Leads</th><th>Check</th><th className="r" title="New paid checks sent for this run">Requested</th>
                <th className="r" title="Already verified, or already being checked by another run. Not charged.">Reused</th><th className="r">Waiting</th>
                <th className="r">Done</th><th className="r">Failed</th><th className="r">Unknown</th><th /></tr></thead>
              {runs.map((r) => {
                const kinds = r.kinds as ('phone' | 'email')[];
                const failed = kinds.reduce((n, k) => n + runCounts(r.counts, r.reused_counts, k).failed, 0);
                return (
                  // One body per run, so the whole run highlights and opens as one.
                  <tbody key={r.id} className="li-runbody" title="Show what this run checked" onClick={() => setOpenRun(r.id)}>
                  {kinds.map((k, i) => {
                    const c = runCounts(r.counts, r.reused_counts, k);
                    return (
                      <tr key={k}>
                        {i === 0 && (
                          <td rowSpan={kinds.length}>
                            <button type="button" className="li-btn li-btn--link" onClick={(e) => { e.stopPropagation(); setOpenRun(r.id); }}>Run #{r.id}</button>
                            {r.force && <div className="li-muted">checked again</div>}
                          </td>
                        )}
                        {i === 0 && <td rowSpan={kinds.length}>{fmtWhen(r.created_at)}</td>}
                        {i === 0 && <td rowSpan={kinds.length} className="r num">{fmtInt(r.lead_count)}</td>}
                        <td>{k === 'phone' ? 'Phones' : 'Emails'}</td>
                        <td className="r num">{fmtInt(c.total)}</td>
                        <td className="r num" title={c.joined ? `${fmtInt(c.joined)} waiting on another run's check` : undefined}>{fmtInt(c.reused + c.joined)}</td>
                        <td className="r num">{fmtInt(c.queued + c.sent)}</td>
                        <td className="r num strong">{fmtInt(c.recorded)}</td>
                        <td className={`r num${c.failed ? ' warn' : ''}`}>{fmtInt(c.failed)}</td>
                        <td className="r num" title="Sent to the provider but no answer was recorded. Not resent, because it may have been charged.">{fmtInt(c.unknown)}</td>
                        {i === 0 && (
                          <td rowSpan={kinds.length}>
                            {failed > 0 && (
                              <button type="button" className="li-btn li-btn--secondary" disabled={retry.isPending}
                                onClick={(e) => { e.stopPropagation(); retry.mutate(r.id); }}>Retry failed</button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  </tbody>
                );
              })}
            </table>
          </div>
          <p className="li-muted li-card__foot">Unknown means the provider was called but no answer came back. Those are never sent again, so nothing is charged twice.</p>
        </Card>
      )}
      {openRun != null && <VerificationRunDialog runId={openRun} onClose={() => setOpenRun(null)} onOpenLead={onOpenLead} />}
    </div>
  );
}

/* ── Duplicate report and export ──────────────────────── */

/** Opens the Export dialog, loading what its filters offer on first open. */
function ExportButton({ batch }: { batch: BatchDetail }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="li-btn li-btn--secondary" onClick={() => setOpen(true)}>Export</button>
      {open && <BatchExportDialog batch={batch} onClose={() => setOpen(false)} />}
    </>
  );
}

function BatchExportDialog({ batch, initial, onClose }: { batch: BatchDetail; initial?: Partial<ExportFilter>; onClose: () => void }) {
  const report = useQuery({ queryKey: leadImportKeys.duplicates(batch.id), queryFn: () => fetchDuplicateReport(batch.id), ...queryPolicies.pagedData });
  const summary = useQuery({ queryKey: leadImportKeys.summary(batch.id), queryFn: () => fetchBatchSummary(batch.id), ...queryPolicies.pagedData });
  return <ExportDialog batchId={batch.id} ownSource={batch.lead_source} report={report.data} reps={summary.data?.by_rep ?? []} initial={initial} onClose={onClose} />;
}

/** [spec v4 C1] Duplicates are leads whose phone came in an earlier import. */
function DuplicateReportCard({ batch }: { batch: BatchDetail }) {
  const report = useQuery({ queryKey: leadImportKeys.duplicates(batch.id), queryFn: () => fetchDuplicateReport(batch.id), ...queryPolicies.pagedData });
  const [exporting, setExporting] = useState<Partial<ExportFilter> | null>(null);
  const r = report.data;
  const sum = (o: Record<string, number>) => Object.values(o).reduce((n, x) => n + Number(x), 0);
  const phones = sum(batch.verification.results.phone);
  const emails = sum(batch.verification.results.email);
  const exportButton = (label: string, f: Partial<ExportFilter>) => (
    <button type="button" className="li-btn li-btn--link" onClick={() => setExporting(f)}>{label}</button>
  );
  return (
    <Card title="Duplicates" subtitle="A duplicate is a lead whose phone came in an earlier import."
      actions={<button type="button" className="li-btn li-btn--secondary" onClick={() => setExporting({})}>Export</button>}>
      {report.isPending ? <p className="li-muted">Counting duplicates…</p> : !r ? <p className="li-muted">The duplicate report could not be loaded.</p> : (
        <div className="li-stack">
          <div className="li-stats li-stats--4" aria-label="Duplicate report">
            <Stat label="Rows" value={fmtInt(batch.delivered_row_count)} sub={`${fmtInt(r.leads)} leads`} />
            <Stat label="Never seen before" value={fmtInt(r.never_seen)} tone="good" sub={exportButton('Export these', { duplicates: 'except' })} />
            <Stat label="Total duplicates" value={fmtInt(r.duplicates)} tone={r.duplicates ? 'warn' : undefined} sub={r.duplicates > 0 && exportButton('Export these', { duplicates: 'only' })} />
            <Stat label="Excluded" value={fmtInt(r.excluded.merchant + r.excluded.no_phone)}
              sub={`${fmtInt(r.excluded.merchant)} merchant, ${fmtInt(r.excluded.dnd)} DND, ${fmtInt(r.excluded.no_phone)} no phone`} />
            <Stat label="Duplicates, same source" value={fmtInt(r.same_source)} sub={`${fmtInt(r.same_source_imports.length)} earlier import${r.same_source_imports.length === 1 ? '' : 's'}`} />
            <Stat label="Duplicates, other sources" value={fmtInt(r.other_source)} sub={`${fmtInt(r.other_sources.length)} source${r.other_sources.length === 1 ? '' : 's'}`} />
            <Stat label="Phones verified" value={fmtInt(phones)} sub={`of ${fmtInt(batch.verification.phones)}`} />
            <Stat label="Emails verified" value={fmtInt(emails)} sub={`of ${fmtInt(batch.verification.emails)}`} />
          </div>
          {(r.same_source_imports.length > 0 || r.other_sources.length > 0) && (
            <div className="li-grid2">
              <div>
                <h4 className="li-subhead">Earlier imports from {batch.source ?? 'this source'}</h4>
                {r.same_source_imports.length === 0 ? <p className="li-muted">None. No lead came from this source before.</p> : (
                  <ul className="li-duplist" aria-label="Earlier imports from this source">
                    {r.same_source_imports.map((i) => (
                      <li key={i.batch}>
                        <Link to={`/Imports?batch=${i.batch}&view=details`}>{i.file_name ?? `Import #${i.batch}`}</Link>
                        <span className="li-muted">{fmtDay(i.batch_date)}</span>
                        <span className="num">{fmtInt(i.leads)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h4 className="li-subhead">Duplicates by other source</h4>
                {r.other_sources.length === 0 ? <p className="li-muted">None.</p> : (
                  <ul className="li-duplist" aria-label="Duplicates by other source">
                    {r.other_sources.map((o) => (
                      <li key={o.lead_source ?? 'none'}>
                        {o.lead_source == null ? <span>{o.source}</span> : exportButton(o.source, { duplicates: 'only', source: o.lead_source })}
                        <span className="num">{fmtInt(o.leads)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          {r.duplicates > 0 && <p className="li-muted">A lead can be a duplicate of more than one source, so the source counts can add up to more than the total.</p>}
        </div>
      )}
      {exporting && <BatchExportDialog batch={batch} initial={exporting} onClose={() => setExporting(null)} />}
    </Card>
  );
}

/* ── Details ──────────────────────────────────────────── */

/** [BIZ v4 #29] Empty merchant fields take values from this import's linked
 * leads; values that differ from the merchant's are kept on the lead. */
function FillCard({ batch }: { batch: BatchDetail }) {
  const queryClient = useQueryClient();
  const [showNever, setShowNever] = useState(false);
  const fill = batch.effects.find((e) => e.kind === 'fill_blanks');
  const never = useQuery({ queryKey: leadImportKeys.neverFill, queryFn: fetchNeverFillFields, enabled: showNever, ...queryPolicies.configuration });
  const run = useMutation({
    mutationFn: () => fillBlanks(batch.id),
    onSuccess: (r) => {
      toast.success(`Filled ${fmtInt(r.filled ?? 0)} fields on ${fmtInt(r.merchants ?? 0)} merchants.`);
      queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch.id) });
      queryClient.invalidateQueries({ queryKey: leadImportKeys.leadsAll });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const off = batch.enrichment_intent !== 'on';
  const d = fill?.detail;
  return (
    <Card title="Fill empty merchant fields">
      <div className="li-stack li-stack--tight" data-testid="li-fill-card">
        <p className="li-muted">Linked merchants get a value from their leads for every field they have empty. A different value the merchant already has stays as it is, and the lead&apos;s value is kept on the lead so it can be looked at later.</p>
        {fill?.state === 'done' && d ? (
            <dl className="li-dl li-dl--cols">
              <dt>Fields filled</dt><dd data-testid="li-fill-filled">{fmtInt(d.filled ?? 0)}</dd>
              <dt>Merchants updated</dt><dd>{fmtInt(d.merchants ?? 0)}</dd>
              <dt title="Lead values that differ from what the merchant already has">Different values kept on leads</dt><dd>{fmtInt(d.conflicts ?? 0)}</dd>
              <dt>Last run</dt><dd>{d.at ? fmtWhen(d.at) : '-'}</dd>
            </dl>
          ) : fill?.state === 'failed' ? <p className="li-warn">The last run failed{d?.error ? `: ${d.error}` : ''}. Merchants were not changed.</p>
          : off ? <p data-testid="li-fill-off">Autofill is off for this import, so committing it leaves merchants as they are. Fill now fills them whenever you choose.</p>
          : !fill ? <p>Runs when the import is committed.</p>
          : <p>Not run yet for this import.</p>}
        {batch.status === 'Committed' && (
          <div>
            <button type="button" className="li-btn li-btn--secondary" disabled={run.isPending} onClick={() => run.mutate()}>
              {run.isPending ? 'Filling...' : fill?.state === 'done' ? 'Run again' : 'Fill now'}
            </button>
            {fill?.state === 'done' && <span className="li-muted"> Picks up leads linked since, by promotion or a resolved review.</span>}
          </div>
        )}
        <div>
          <button type="button" className="li-btn li-btn--link" onClick={() => setShowNever((v) => !v)} aria-expanded={showNever}>
            {showNever ? 'Hide fields that are never filled' : 'Fields that are never filled'}
          </button>
          {showNever && (
            <ul className="li-list" data-testid="li-never-fill">
              {(never.data ?? []).map((f) => <li key={f.field_name}><strong>{f.ui_label}</strong> <span className="li-muted">{f.reason}</span></li>)}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

function DetailsTab({ batch, onOpenLeads }: { batch: BatchDetail; onOpenLeads: () => void }) {
  const [fileOpen, setFileOpen] = useState(false);
  const exportRun = useMutation({
    mutationFn: async (kind: 'excluded' | 'credit' | 'secondary') => {
      const rows = kind === 'excluded' ? await fetchExcludedExport(batch.id) : kind === 'credit' ? await fetchCreditExport(batch.id) : await fetchSecondaryReport(batch.id);
      if (!rows.length) { toast.message('Nothing to export.'); return; }
      downloadCsv(`import-${batch.id}-${kind}.csv`, rows);
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const cost = costOf(batch);
  const skipped = batch.skipped_missing_phone_count + batch.skipped_invalid_phone_count;
  const sheets = batch.sheets.filter((x) => x.selected);

  return (
    <div className="li-stack">
      {batch.status === 'Committed' && (
        <Card title="How the rows became leads">
          <RowLedger rows={batch.delivered_row_count} leads={batch.observation_count} merged={batch.collapsed_row_count} skipped={skipped}
            openable={['rows', 'leads']} onOpen={(part) => (part === 'rows' ? setFileOpen(true) : onOpenLeads())} />
          {fileOpen && <FilePreviewDialog batchId={batch.id} name={batch.file?.original_filename ?? batch.file_name ?? 'Uploaded file'} bytes={batch.file?.byte_size} onClose={() => setFileOpen(false)} />}
          <dl className="li-dl li-dl--cols">
            <dt>Linked to a merchant</dt><dd>{fmtInt(batch.linked_count)}</dd>
            <dt>Sent for review</dt><dd>{fmtInt(batch.held_count)}</dd>
          </dl>
          <p className="li-muted">Skipped rows had no usable phone. They are counted here and stay in the original file.</p>
        </Card>
      )}
      {batch.status === 'Committed' && <DuplicateReportCard batch={batch} />}

      <div className="li-grid2">
        <Card title="Delivery">
          <dl className="li-dl">
            <dt>File</dt><dd><FileChip batchId={batch.id} name={batch.file?.original_filename ?? batch.file_name} bytes={batch.file?.byte_size} /></dd>
            <dt>Sheets</dt><dd>{sheets.map((x) => x.sheet_name).join(', ') || '-'}</dd>
            {sheets.map((x) => (
              <Fragment key={x.id}>
                <dt>Columns of {x.sheet_name}</dt>
                <dd>{x.mapping ? `${x.mapping.profile_name}, version ${x.mapping.version_number}${x.mapping.origin === 'ai' ? ', suggested by AI' : ''}` : '-'}</dd>
              </Fragment>
            ))}
            <dt title="Identifies this exact content for duplicate checks">File fingerprint</dt><dd className="li-mono li-ellipsis" title={batch.content_fingerprint ?? ''}>{batch.content_fingerprint?.slice(0, 16) ?? '-'}</dd>
          </dl>
          <SourceLocationNote location={batch.source_location} />
          {batch.attempts.length > 0 && (
            <p className="li-warn">This file was uploaded again and rejected as a duplicate on {batch.attempts.map((a) => fmtWhen(a.attempted_at)).join(', ')}.</p>
          )}
        </Card>
        <Card title="Cost">
          <div className="li-stack li-stack--tight">
            <ImportCostFields batch={batch} sourceChosen={!!batch.lead_source} />
            {cost > 0 && batch.status === 'Committed' && (
              <dl className="li-dl">
                <dt>Per lead</dt><dd>{fmtMoney(batch.observation_count ? cost / batch.observation_count : null)}</dd>
                <dt>Per new lead</dt><dd>{fmtMoney(batch.new_identity_count ? cost / batch.new_identity_count : null)}</dd>
              </dl>
            )}
          </div>
        </Card>
      </div>

      <div className="li-grid2">
        <FillCard batch={batch} />
        <Card title="Exports">
          <div className="li-stack li-stack--tight">
            <div><button type="button" className="li-btn li-btn--secondary" disabled={exportRun.isPending} onClick={() => exportRun.mutate('excluded')}>Leads not assignable</button></div>
            <div><button type="button" className="li-btn li-btn--secondary" disabled={exportRun.isPending} onClick={() => exportRun.mutate('credit')}>Credit request for failed verification</button></div>
            <div><button type="button" className="li-btn li-btn--secondary" disabled={exportRun.isPending} onClick={() => exportRun.mutate('secondary')}>Email and EIN conflicts</button></div>
          </div>
        </Card>
      </div>

      <Card title="Activity">
        <ul className="li-activity">
          {batch.events.map((e, i) => <li key={i}><span className="li-activity__when">{fmtWhen(e.at)}</span> {e.kind.replace(/_/g, ' ')}{e.actor ? <span className="li-muted">, {e.actor}</span> : null}</li>)}
        </ul>
      </Card>
    </div>
  );
}
